'use client';

import CardCategory from '@/components/sections/builder/elements/body/collection-product-grid/components/card-category';
import { FilterSortType } from '@/components/sections/builder/elements/body/collection-product-grid/content';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { AVAILABILITY_OPTIONS } from '@/constant/data';
import { formatIDR, truncateText } from '@/lib/utils';
import { ThemeColorSchemeType, ThemeType } from '@/stores/builder';
import { BaseProductCategory } from '@/types/http/product/common';
import { Star, StarIcon, X } from 'lucide-react';

export type ProductFilterType = {
  filterLayout: 'horizontal' | 'vertical' | 'drawer';
  enableFilter: boolean;
  enableSort: boolean;
  showFilterCategory?: boolean;
  showFilterAvailability?: boolean;
  showFilterPrice?: boolean;
  colorScheme?: ThemeColorSchemeType;
};

interface VerticalFilterProps {
  data: ProductFilterType;
  categories?: BaseProductCategory[];
  filter: FilterSortType;
  price: { min: string; max: string };
  setCategory?: React.Dispatch<React.SetStateAction<string>>;
  handleCheckedChange: (type: 'inStock' | 'outStock') => void;
  handlePriceChange: (type: 'min' | 'max', value: string) => void;
  handleSelectCategory?: (id: string, name: string) => void;
  handleRemoveFilter: (name: string) => void;
  handleResetFilter: () => void;
  isLoadingCategory?: boolean;
  isFilterCategory?: boolean;
  handleRatingFilter: (rating: number) => void;
  isActiveReviewProduct?: boolean;
  isPreview?: boolean;
  theme: ThemeType;
}

export default function VerticalFilter({
  data,
  categories,
  filter,
  price,
  setCategory,
  handleCheckedChange,
  handlePriceChange,
  handleSelectCategory,
  handleRemoveFilter,
  handleResetFilter,
  isLoadingCategory,
  handleRatingFilter,
  isFilterCategory = true,
  isActiveReviewProduct,
  isPreview = false,
  theme,
}: VerticalFilterProps) {
  const starColor = theme?.productRating?.starColor || DEFAULT_THEME_COLOR_SCHEME.text;
  return (
    <div className='mb-4 flex w-full flex-col gap-3 pl-3 pt-3'>
      <div className='flex items-center justify-between'>
        <h2 className='text-sm font-medium'>Filter: </h2>
        {(filter.availability?.length || Number(price.min) > 0 || Number(price.max) > 0) &&
          data.enableFilter &&
          data.filterLayout === 'vertical' && (
            <Button
              variant='link'
              className='text-xs underline'
              onClick={handleResetFilter}
              style={{ color: data.colorScheme?.btnSolidBackgroud }}
            >
              Remove all
            </Button>
          )}
      </div>
      {data.filterLayout === 'vertical' && (
        <div className='flex flex-row justify-start gap-3 md:flex-col'>
          {data.enableFilter && filter.categoryId && (data.showFilterCategory ?? true) && (
            <div>
              <Button
                className='inline-flex items-center gap-2 rounded-full p-2 px-3 text-xs md:text-sm'
                onClick={() => handleRemoveFilter('category')}
                variant='outline'
              >
                <span>{truncateText(filter.categoryName as string, 35)}</span>
                <X className='h-3 w-3' />
              </Button>
            </div>
          )}
          {data.enableFilter &&
            filter.availability?.includes('1') &&
            (data.showFilterAvailability ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs md:text-sm'
                  onClick={() => handleRemoveFilter('inStock')}
                >
                  <span>Availability: In stock</span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
          {data.enableFilter &&
            filter.availability?.includes('0') &&
            (data.showFilterAvailability ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs md:text-sm'
                  onClick={() => handleRemoveFilter('outStock')}
                >
                  <span>Availability: Out of stock</span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
          {price.min !== '' && price.max !== '' && (data.showFilterPrice ?? true) && (
            <div className='mb-2'>
              <Button
                variant='outline'
                className='inline-flex items-center gap-2 rounded-full text-xs md:text-sm'
                onClick={() => handleRemoveFilter('price')}
              >
                {formatIDR(price.min)} - {formatIDR(price.max)}
                <X className='h-3 w-3' />
              </Button>
            </div>
          )}
          {filter.rating && (
            <div>
              <Button
                variant='outline'
                className='inline-flex items-center gap-x-1 gap-y-2 rounded-full text-xs md:text-sm'
                onClick={() => handleRemoveFilter('rating')}
              >
                <span>Rating:</span>
                <StarIcon className='h-4 w-4 text-yellow-300' fill='currentColor' />
                <span className=''>{filter.rating}</span>
                <X className='ml-2 h-3 w-3' />
              </Button>
            </div>
          )}
        </div>
      )}
      <Separator />
      {(data.showFilterCategory ?? true) && isFilterCategory && (
        <CardCategory
          colorScheme={data.colorScheme}
          categories={categories || []}
          filter={filter}
          isLoading={isLoadingCategory as boolean}
          setCategory={setCategory as React.Dispatch<React.SetStateAction<string>>}
          onSelectCategory={handleSelectCategory as (id: string, name: string) => void}
        />
      )}
      {(data.showFilterAvailability ?? true) && (
        <Select>
          <SelectTrigger className='w-full'>
            <SelectValue placeholder='Availability' />
          </SelectTrigger>
          <SelectContent>
            <div className='flex flex-col space-y-3 p-3'>
              {AVAILABILITY_OPTIONS.map(({ id, label, value }) => {
                const isChecked = filter.availability?.includes(value);
                const checkedStyle = isChecked
                  ? {
                      color: 'white',
                      borderColor: data.colorScheme?.btnSolidBackgroud,
                      backgroundColor: data.colorScheme?.btnSolidBackgroud,
                    }
                  : { borderColor: data.colorScheme?.btnSolidBackgroud };

                return (
                  <Label key={id} className='flex items-center gap-2 font-normal'>
                    <Checkbox
                      id={id}
                      checked={isChecked}
                      onCheckedChange={() =>
                        handleCheckedChange(value === '1' ? 'inStock' : 'outStock')
                      }
                      style={checkedStyle}
                      isCustom
                    />{' '}
                    {label}
                  </Label>
                );
              })}
            </div>
          </SelectContent>
        </Select>
      )}
      {(data.showFilterPrice ?? true) && (
        <div className='flex flex-col'>
          <h2 className='mt-2 text-sm'>Price:</h2>
          <div className='flex w-full items-center gap-2'>
            <Input
              placeholder='From'
              value={price.min}
              onChange={(e) => handlePriceChange('min', e.target.value)}
            />
            <Input
              placeholder='To'
              value={price.max}
              onChange={(e) => handlePriceChange('max', e.target.value)}
            />
          </div>
        </div>
      )}
      {(isPreview || isActiveReviewProduct) && (
        <div className='flex flex-col'>
          <h2 className='mt-2 text-sm'>Rating:</h2>
          <div className='flex w-full items-center gap-2'>
            {[5, 4, 3, 2, 1].map((rating) => {
              const isSelected = filter.rating === rating;
              return (
                <Button
                  key={rating}
                  variant={isSelected ? 'default' : 'outline'}
                  className='gap-x-1 px-[11px]'
                  onClick={() => handleRatingFilter(rating)}
                  style={
                    isSelected
                      ? {
                          backgroundColor: data.colorScheme?.btnSolidBackgroud,
                          color: data.colorScheme?.btnSolidlabel,
                        }
                      : {}
                  }
                >
                  <Star
                    className='h-4 w-4 '
                    style={{
                      color: starColor,
                      fill: starColor,
                    }}
                  />
                  <span>{rating}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
